import { TemplateTypes, CssClass } from "Enums";
import { IControl } from "Controls/Interfaces";
import { Navigation, Util } from "Core";
import { IDashboardItemConfig, IDashboardDefinition, IUserGridConfigDefinition, IDashboardColumnDefinition } from "Grids/Interfaces";
import { GridSettings } from "Configuration";
import { IDataRow } from "Entities";
import { TextBlockService, CommandService, ComboboxService, GridDefinitionService, DatabaseImageService } from "Service";
import { ControlEventTypes, GridEventTypes } from "Core/Events/EventTypes";
import { EventArgs } from "Core/Events";
import { BaseForm } from "./BaseForm";

/**
 * ## Dashboard
 *
 * Show multiple containers with custom content.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a Dashboard with the name GridName.
 * // In script:
 * @@grd:GridName@@
 *
 * // On page:
 * $dms("GridName")
 *
 * // First Dashboard on page:
 * $dms(".dms-dashboard")
 * ```
 */
export class Dashboard extends BaseForm {
    /**
     * @hidden
     */
    protected dashboardDefinition: IDashboardDefinition;

    /**
     * @hidden
     */
    public dashboardItemState: {
        [columnname: string]: { Row: string; Column: string; Height: string; Width: string; IsCollapsed: boolean; IsHidden: boolean };
    } = {};

    /**
     * @hidden
     */
    public static wrapperTemplate: string = require("./Templates/Dashboard.html");

    /**
     * @hidden
     */
    public static contentTemplate: string = require("./Templates/DashboardContent.html");

    /**
     * @hidden
     */
    public static columnTemplate: string = require("./Templates/DashboardColumn.html");

    /**
     * @hidden
     * The gridstack instance.
     */
    private gridstack: GridStack;

    private cellHeight = 60;
    private verticalMargin = 10;

    /**
     * Create a new Dashboard.
     * @param formDefinition FormDefinition.
     * @param gridSettings GridSettings.
     * @hidden
     */
    constructor(
        dashboardDefinition: IDashboardDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        comboboxService: ComboboxService,
        commandService: CommandService,
        textBlockService: TextBlockService,
        gridDefinitionService: GridDefinitionService,
        databaseImageService: DatabaseImageService
    ) {
        super(
            dashboardDefinition,
            textBlockCategory,
            gridSettings,
            comboboxService,
            commandService,
            gridDefinitionService,
            textBlockService,
            databaseImageService
        );
        this.dashboardDefinition = dashboardDefinition;
        this.isAutoResize = Util.stringIsNullOrEmpty(dashboardDefinition.GridHeight);
    }

    /**
     * Sets the jQuery-Element to initialize the grid.
     * @param element JQuery-Wrapper in which the grid is initialized.
     * @hidden
     */
    protected async setElementInternal(element: JQuery): Promise<void> {
        await this.textBlockService.getAll();

        // Initialize controls
        this.fields = this.initializeColumns(this.dashboardDefinition.Columns, this.fields);
    }

    /**
     * Initialize the dashboard.
     * @hidden
     */
    protected initialize(): void {
        this.initializeControlTemplates(<IDataRow>(<any>this._viewModel));
        this.disposeEditContext();
        this.editContext.initialize(this.editMode);

        this.jQueryElement.empty();

        if (this.dashboardDefinition.CanHideColumns) {
            this.jQueryElement.on("click", ".dms-dashboard-hide", e => this.onItemHide(e));
        }
        this.jQueryElement.on("click", ".dms-dashboard-fullscreen-button", e => this.onFullscreen(e));

        // add dashboard item states for all available columns
        for (let i = 0, colLen = this.dashboardDefinition.Columns.length; i < colLen; i++) {
            let columnDefinition = this.dashboardDefinition.Columns[i];
            this.dashboardItemState[columnDefinition.ColumnName] = {
                IsCollapsed: false,
                Row: columnDefinition.Row,
                Column: columnDefinition.Column,
                IsHidden: false,
                Width: columnDefinition.Width || "6",
                Height: columnDefinition.Height || "4"
            };
        }

        // overwrite the config for columns which are in the usergridconfig
        if (this.currentUserGridConfig) {
            try {
                let config = this.currentUserGridConfig.Configuration;
                let usergridConfigurations: IDashboardItemConfig[] = JSON.parse(config);
                for (let i = 0, len = usergridConfigurations.length; i < len; i++) {
                    let usergridConfiguration = usergridConfigurations[i];
                    this.dashboardItemState[usergridConfiguration.ColumnName] = {
                        IsCollapsed: usergridConfiguration.IsCollapsed,
                        IsHidden: usergridConfiguration.IsHidden,
                        Row: usergridConfiguration.Row,
                        Column: usergridConfiguration.Column,
                        Height: usergridConfiguration.Height,
                        Width: usergridConfiguration.Width
                    };
                }
            } catch (e) {
                console.log("error in usergridconfig");
            }
        }
        let formContainer = "";
        let template = kendo.template(this.gridSettings.getTemplate(TemplateTypes.Content), { useWithBlock: false });
        formContainer = template(this);

        this.jQueryElement.append(formContainer);

        let options: IGridstackOptions = {
            verticalMargin: this.verticalMargin,
            cellHeight: this.cellHeight,
            width: 12,
            animate: true,
            float: false,
            disableDrag: !this.dashboardDefinition.CanReorderColumns,
            disableResize: !this.dashboardDefinition.CanResizeColumns,
            resizable: {
                handles: "e, se, s, sw, w"
            },
            handle: ".dms-dashboarditem-header" // draggable handle
        };
        this.gridstack = this.jQueryElement.gridstack(options).data("gridstack");
        this.jQueryElement.on("gsresizestop", (e, elem) => this.onItemResize(e, elem));

        this.initializeJQueryControls();

        kendo.bind(this.jQueryElement.parent(), this._viewModel);
        this.initializeTooltips();

        this.editContext.setDataRowFunction(() => this.getData());
        this.editContext.load(this.textBlockCategory, this.jQueryElement);
        if (this.dashboardDefinition.CanReorderColumns) {
            this.jQueryElement.addClass("dms-reorder");
        }
    }

    /**
     * Occurs after a dashboard-item was resized.
     * @param e Event.
     * @param elem Item which was resized.
     */
    private onItemResize(e: JQuery.TriggeredEvent, elem: HTMLElement) {
        let item = $(elem);
        let columnName = item.data("dms-columnname");
        let column = this.dmsColumnDictionary[columnName];
        let height = this.getCalculatedHeightFromItem(item);

        this.resizeColumn(column, height);
    }

    /**
     * Occurs after a user clicked on the hide button of a dashboard item.
     * @param e Event.
     */
    private onItemHide(e: JQuery.ClickEvent) {
        let dashboardItem = $(e.target).closest(".dms-dashboarditem");
        let columnName = dashboardItem.attr("data-dms-columnname");

        this.gridstack.removeWidget(dashboardItem[0], false);
        dashboardItem.addClass(CssClass.hidden);
        this.dashboardItemState[columnName].IsHidden = true;
    }

    /**
     * Occurs after a user clicked on the maximize or minimize button of a dashboard item.
     * @param e Event.
     */
    private onFullscreen(e: JQuery.ClickEvent): void {
        let dashboardItem = $(e.target).closest(".dms-dashboarditem");
        let iconElement = dashboardItem.find(".dms-dashboarditem-icon-fullscreen");

        if (dashboardItem.hasClass("dms-dashboard-fullscreen")) {
            // leave fullscreen
            dashboardItem.removeClass("dms-dashboard-fullscreen").addClass("grid-stack-item");
            iconElement.removeClass("k-i-window-minimize").addClass("k-i-window-maximize");
            dashboardItem.css("top", "");
            this.gridstack.enable();
            dashboardItem.find(".dms-dashboard-hide").show();
        } else {
            // go in fullscreen
            dashboardItem.addClass("dms-dashboard-fullscreen").removeClass("grid-stack-item");
            iconElement.removeClass("k-i-window-maximize").addClass("k-i-window-minimize");

            this.gridstack.disable();
            let calculatedHeight = $(".dms-menu-wrapper").outerHeight() + $(".dms-mapping-header").outerHeight();
            dashboardItem.css("top", calculatedHeight + "px");
            dashboardItem.find(".dms-dashboard-hide").hide();
        }

        // resize the controls in the item with the actual height.
        let columnName = dashboardItem.data("dms-columnname");
        let column = this.dmsColumnDictionary[columnName];
        let height = dashboardItem.outerHeight() - dashboardItem.find(".k-header").outerHeight();

        this.resizeColumn(column, height);
    }

    /**
     * Register additional control events.
     * @param ctl Control.
     * @hidden
     */
    protected registerControlEvents(ctl: IControl): void {
        ctl.on(ControlEventTypes.Resolve, e => this.onResolveScript(e.sender, <string>e.data));
    }

    /**
     * Get the height of the item by calculating the stored gridstack height with the item height.
     * @param item
     */
    private getCalculatedHeightFromItem(item: JQuery): number {
        let itemHeight = Number(item.attr("data-gs-height"));
        let height = itemHeight * this.cellHeight + (itemHeight - 1) * this.verticalMargin - item.find(".k-header").outerHeight();
        return height;
    }

    /**
     * Initialize all controls rendered in the template.
     * @hidden
     */
    protected initializeJQueryControls(): void {
        let keys = Object.keys(this.dmsColumnDictionary);
        for (let i = 0, cnt = keys.length; i < cnt; i++) {
            let column = this.dmsColumnDictionary[keys[i]];
            let item = this.jQueryElement.find(`.dms-dashboarditem[data-dms-columnname='${column.ColumnName}']`);
            let availableHeight = this.getCalculatedHeightFromItem(item);
            this.resizeColumn(column, availableHeight);
        }

        this.initializeControlsInContainer(this.jQueryElement, this.dmsControlDictionary);
    }

    /**
     * Resize all controls in given column to fit the available height.
     * @param column The column which contains all controls to be resized.
     * @param availableHeight The total height available in this dashboard item.
     */
    private resizeColumn(column: IDashboardColumnDefinition, availableHeight: number) {
        let remainingHeight = availableHeight;
        let autoHeightCount = column.Controls.length;
        for (let i = 0, len = column.Controls.length; i < len; i++) {
            let controlDefinition = column.Controls[i];
            if (controlDefinition.Height !== undefined) {
                let control = this.dmsControlDictionary[controlDefinition.ControlID];
                let itemHeight = Number(controlDefinition.Height);
                control.setHeight(itemHeight);
                remainingHeight -= itemHeight;
                autoHeightCount--;
            }
        }
        if (autoHeightCount > 0) {
            for (let i = 0, len = column.Controls.length; i < len; i++) {
                let controlDefinition = column.Controls[i];
                if (controlDefinition.Height === undefined) {
                    let control = this.dmsControlDictionary[controlDefinition.ControlID];
                    let itemHeight = remainingHeight / autoHeightCount;
                    control.setHeight(itemHeight);
                }
            }
        }
    }

    /**
     * Loads data for the dashboard.
     * @hidden
     */
    protected async loadData(): Promise<void> {
        // Initialize view model
        this._viewModel = new kendo.data.Model(this.fields);
        this.setBusy(true);
        try {
            let data = await this.commandService.getFormData(this.gridSettings.dataUrlGet, this.dashboardDefinition.GridID);
            $.extend(this._viewModel, data); // merge the default values with the data received by service.
            this.initialize();
            this.trigger(GridEventTypes.DataBound, new EventArgs(this, null));
        } catch (error) {
            Navigation.errorAsync(error, "Error");
            this.setBusy(false);
        }
    }

    /**
     * Reloads data of this grid.
     * @hidden
     */
    protected reloadInternal(): Promise<void> {
        this.show();
        return this.loadData();
    }

    /**
     * Apply selected UserGridConfig.
     * @hidden
     */
    public async applyUserGridConfig(config: IUserGridConfigDefinition): Promise<void> {
        await super.applyUserGridConfig(config);
        this.dispose();
        this.jQueryElement.empty();
    }

    /**
     * Create UserGridConfig for the current state.
     * @hidden
     */
    public createUserGridConfig(): string {
        let sortedDefinition: IDashboardItemConfig[] = [];
        let items = this.jQueryElement.find(".dms-dashboarditem");
        for (let i = 0, len = items.length; i < len; i++) {
            let child = $(items[i]);
            let columnName = child.attr("data-dms-columnname");
            sortedDefinition.push({
                Row: child.attr("data-gs-y"),
                Column: child.attr("data-gs-x"),
                ColumnName: columnName,
                Width: child.attr("data-gs-width"),
                Height: child.attr("data-gs-height"),
                IsCollapsed: this.dashboardItemState[columnName] ? this.dashboardItemState[columnName].IsCollapsed : false,
                IsHidden: this.dashboardItemState[columnName] ? this.dashboardItemState[columnName].IsHidden : false
            });
        }
        return JSON.stringify(sortedDefinition);
    }

    /**
     * Dispose the grid and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        this.disposeEditContext();
        if (this.gridstack) {
            this.gridstack.destroy(false);
            this.gridstack = null;
        }
    }
}
